import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL } from '../config/api';
import { trackEvent } from '../utils/analytics';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { formatISODate } from '../components/badges';
import { usePageMeta } from '../hooks/usePageMeta';
import {
  MARKER_TITLES,
  ParsedMrl,
  mrlText,
  normName,
  parseMrl,
  shortReg,
  splitResidueName,
  tidyName,
} from '../utils/pesticide';

/* ── API shapes ─────────────────────────────────────────────────────── */

interface DiffRow {
  product_id: string;
  product_name: string | null;
  product_code: string | null;
  pesticide_residue_id: string | null;
  pesticide_residue_name: string | null;
  mrl_value: string | null;
  application_date: string | null;
  entry_into_force_date: string | null;
  prev_mrl_value: string | null;
  prev_regulation_number: string | null;
  prev_application_date: string | null;
}

interface RegulationData {
  regulation: string;
  regulation_url: string | null;
  application_date: string | null;
  entry_into_force_date: string | null;
  status: 'in-force' | 'upcoming' | 'superseded';
  rows: DiffRow[];
}

/* ── View model ─────────────────────────────────────────────────────── */

type Effect = 'lower' | 'higher' | 'new' | 'changed' | 'unchanged';
type Filter = 'changed' | 'lower' | 'higher' | 'new' | 'all';

interface Row extends DiffRow {
  substance: string;
  markers: string[];
  product: string;
  mrl: ParsedMrl;
  prev: ParsedMrl | null;
  effect: Effect;
  key: string;
}

const EFFECT_LABEL: Record<Effect, string> = {
  lower: 'Lower · stricter',
  higher: 'Higher · lenient',
  new: 'New',
  changed: 'Changed',
  unchanged: 'Unchanged',
};

const STATUS_LABEL: Record<RegulationData['status'], string> = {
  'in-force': 'In force',
  upcoming: 'Upcoming',
  superseded: 'No longer applicable',
};

const PAGE = 300;

async function fetchRegulation(regulationNumber: string): Promise<RegulationData> {
  const res = await fetch(`${API_BASE_URL}/api/regulation/${encodeURIComponent(regulationNumber)}`, {
    cache: 'no-store',
  });
  if (!res.ok) throw new Error(`Could not load this regulation (HTTP ${res.status}).`);
  const json = await res.json();
  if (!json.ok) throw new Error('The server could not load this regulation.');
  return {
    regulation: json.regulation,
    regulation_url: json.regulation_url ?? null,
    application_date: json.application_date ?? null,
    entry_into_force_date: json.entry_into_force_date ?? null,
    status: json.status in STATUS_LABEL ? json.status : 'in-force',
    rows: json.rows ?? [],
  };
}

function effectOf(cur: ParsedMrl, prev: ParsedMrl | null): Effect {
  // No earlier value, whether no earlier row or one left empty, means the pair had
  // no limit before this regulation: New, with "No value" as the previous MRL.
  if (!prev || prev.raw == null) return 'new';
  if ((cur.raw ?? '') === (prev.raw ?? '')) return 'unchanged';
  if (cur.value != null && prev.value != null) {
    if (cur.value < prev.value) return 'lower';
    if (cur.value > prev.value) return 'higher';
    return 'changed'; // same number, different marker (e.g. 0.01 -> 0.01*)
  }
  // "No MRL required" replaced by a number is stricter; the reverse is more lenient.
  if (prev.kind === 'exempt' && cur.value != null) return 'lower';
  if (cur.kind === 'exempt' && prev.value != null) return 'higher';
  return 'changed';
}

function toRows(list: DiffRow[]): Row[] {
  return list.map(r => {
    const { name, markers } = splitResidueName(r.pesticide_residue_name);
    const mrl = parseMrl(r.mrl_value);
    const prev = r.prev_mrl_value == null ? null : parseMrl(r.prev_mrl_value);
    const product = tidyName(r.product_name);
    return {
      ...r,
      substance: name || 'Unknown substance',
      markers,
      product,
      mrl,
      prev,
      effect: effectOf(mrl, prev),
      key: normName(`${name} ${product} ${r.product_code ?? ''}`),
    };
  });
}

/* ── Page ───────────────────────────────────────────────────────────── */

export default function RegulationPage() {
  const { regulationNumber = '' } = useParams<{ regulationNumber: string }>();

  const { data, isLoading, isError, error } = useQuery<RegulationData, Error>(
    ['regulation', regulationNumber],
    () => fetchRegulation(regulationNumber),
    { enabled: !!regulationNumber },
  );

  const [filter, setFilter] = useState<Filter>('changed');
  const [search, setSearch] = useState('');
  const [limit, setLimit] = useState(PAGE);

  useEffect(() => {
    setFilter('changed');
    setSearch('');
    setLimit(PAGE);
  }, [regulationNumber]);

  useEffect(() => {
    if (data) trackEvent('Regulation View', { regulation: regulationNumber });
  }, [data, regulationNumber]);

  usePageMeta({
    title: data ? `${data.regulation}: EU MRL changes` : 'Regulation',
    description: data
      ? `Which EU pesticide maximum residue levels ${data.regulation} lowered, raised or added, product by product, with the date it applies.`
      : null,
    noindex: isError,
  });

  const rows = useMemo(() => (data ? toRows(data.rows) : []), [data]);

  const counts = useMemo(() => {
    const c = { lower: 0, higher: 0, new: 0, changed: 0, unchanged: 0 };
    const substances = new Set<string>();
    const products = new Set<string>();
    for (const r of rows) {
      c[r.effect]++;
      if (r.pesticide_residue_id) substances.add(r.pesticide_residue_id);
      products.add(r.product_id);
    }
    return {
      ...c,
      changedTotal: c.lower + c.higher + c.new + c.changed,
      all: rows.length,
      substances: substances.size,
      products: products.size,
    };
  }, [rows]);

  const shown = useMemo(() => {
    const term = normName(search);
    return rows.filter(r => {
      if (filter === 'changed' && r.effect === 'unchanged') return false;
      if (filter === 'lower' && r.effect !== 'lower') return false;
      if (filter === 'higher' && r.effect !== 'higher') return false;
      if (filter === 'new' && r.effect !== 'new') return false;
      if (term && !r.key.includes(term)) return false;
      return true;
    });
  }, [rows, filter, search]);

  useEffect(() => {
    setLimit(PAGE);
  }, [filter, search]);

  if (!regulationNumber) return <p className="pest-page">No regulation specified.</p>;
  if (isLoading) return <LoadingSkeleton rows={8} />;
  if (isError || !data) {
    return <div className="error-state">{error?.message ?? 'Could not load this regulation.'}</div>;
  }

  const visible = shown.slice(0, limit);
  const filters: Array<[Filter, string]> = [
    ['changed', `Changed (${counts.changedTotal})`],
    ['lower', `Lower · stricter (${counts.lower})`],
    ['higher', `Higher · lenient (${counts.higher})`],
    ['new', `New (${counts.new})`],
    ['all', `All rows (${counts.all})`],
  ];

  return (
    <div className="pest-page">
      <div className="pest-page__head">
        <div>
          <div className="pest-eyebrow">Regulation</div>
          <h1 className="pest-page__title">{data.regulation}</h1>
          <div className="pest-head-meta">
            <span className={`pest-pill pest-pill--${data.status}`}>
              {STATUS_LABEL[data.status]}
            </span>
            {data.application_date && (
              <span className="pest-subtle">
                {data.status === 'superseded' ? 'Applied from' : 'Applies from'} {formatISODate(data.application_date)}
              </span>
            )}
            {data.entry_into_force_date && (
              <span className="pest-subtle">
                {data.status === 'superseded' ? 'Entered into force' : 'In force since'} {formatISODate(data.entry_into_force_date)}
              </span>
            )}
            {data.regulation_url && (
              <a className="reg-eurlex-btn" href={data.regulation_url} target="_blank" rel="noopener noreferrer">
                View on EUR-Lex ↗
              </a>
            )}
          </div>
        </div>
        <div className="pest-glance">
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.lower}</div>
            <div className="pest-glance__label">Lower · stricter</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.higher}</div>
            <div className="pest-glance__label">Higher · lenient</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.new}</div>
            <div className="pest-glance__label">New</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.substances}</div>
            <div className="pest-glance__label">Substances</div>
          </div>
          <div className="pest-glance__item">
            <div className="pest-glance__value">{counts.products}</div>
            <div className="pest-glance__label">Products</div>
          </div>
        </div>
      </div>

      <section className="pest-card" aria-label="What this regulation changed">
        <div className="pest-card__head">
          <h2 className="pest-card__title">What changed · {shown.length}</h2>
          <div className="pest-chips" role="group" aria-label="Rows to show">
            {filters.map(([f, label]) => (
              <button
                key={f}
                type="button"
                className={`pest-chip${filter === f ? ' pest-chip--active' : ''}`}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="pest-toolbar">
          <input
            type="search"
            className="pest-toolbar__filter"
            placeholder="Filter substance or product..."
            aria-label="Filter rows"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        <div className="pest-table-wrap">
          <table className="pest-table">
            <thead>
              <tr>
                <th scope="col">Substance</th>
                <th scope="col">Product</th>
                <th scope="col">Previous MRL</th>
                <th scope="col">New MRL</th>
                <th scope="col">Effect</th>
                <th scope="col">Applies from</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => (
                <tr key={`${r.product_id}-${r.pesticide_residue_id}-${r.mrl_value ?? ''}`} className="pest-row">
                  <td>
                    <div className="pest-subst">
                      {r.pesticide_residue_id ? (
                        <Link className="pest-subst__name" to={`/substance/${encodeURIComponent(r.pesticide_residue_id)}`}>
                          {r.substance}
                        </Link>
                      ) : (
                        <span className="pest-subst__name">{r.substance}</span>
                      )}
                      {r.markers.map(m => (
                        <abbr
                          key={m}
                          className="pest-marker"
                          title={MARKER_TITLES[m] ?? 'Marker from the EU Pesticides Database'}
                        >
                          {m}
                        </abbr>
                      ))}
                    </div>
                  </td>
                  <td>
                    <Link className="pest-subst__name" to={`/product/${encodeURIComponent(r.product_id)}`}>
                      {r.product}
                    </Link>
                    {r.product_code && <div className="pest-subtle pest-mono">{r.product_code}</div>}
                  </td>
                  <td className="pest-mono pest-prev">
                    {r.prev ? mrlText(r.prev) : r.prev_regulation_number ? <span className="pest-subtle">No value</span> : <span className="pest-dash">—</span>}
                    {r.prev_regulation_number && (
                      <div className="pest-subtle">{shortReg(r.prev_regulation_number)}</div>
                    )}
                  </td>
                  <td className={`pest-mono pest-mrl pest-mrl--${r.mrl.kind}`}>{mrlText(r.mrl)}</td>
                  <td>
                    <span className={`pest-pill pest-pill--${r.effect}`}>{EFFECT_LABEL[r.effect]}</span>
                  </td>
                  <td className="pest-subtle">{formatISODate(r.application_date) || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {shown.length === 0 && (
            <div className="pest-empty">
              {rows.length === 0
                ? 'No MRL rows are published for this regulation yet.'
                : 'No rows match this filter.'}
            </div>
          )}
          {shown.length > visible.length && (
            <div className="pest-more">
              <button type="button" className="pest-btn" onClick={() => setLimit(l => l + PAGE)}>
                Show {Math.min(PAGE, shown.length - visible.length)} more
              </button>
              <span className="pest-subtle">
                {visible.length} of {shown.length} rows
              </span>
            </div>
          )}
        </div>
      </section>

      <p className="mrl-footnote">
        "Lower" means the new MRL is a smaller number, so compliance is harder. "Higher" means a larger number and
        a more lenient limit. "New" means no earlier MRL value was recorded for that product and substance. * MRL set at the limit of analytical determination (LOD).
      </p>
    </div>
  );
}
